import React from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Check2 from 'components/shared/Icon/icons/Check2';
import ListBullets from 'components/shared/Icon/icons/ListBullets';
import { EOrganizedBy } from 'detailsv2/structure';
import { itinerariesItemDataSelector } from 'store/itinerariesItem/selectors';
import { updateItinerariesItemThunk } from 'store/itinerariesItem/thunks';
import { setScheduleTypeAction, updateDatesByDaysAction, updateDaysByDatesAction } from 'store/itineraryDetails';
import { getItineraryScheduleSelector, getScheduleItemsSelector } from 'store/itineraryDetails/selectors';
import { appHasChangesSelector } from 'store/unsavedChanges/selectors';
import { isSchedulesEmpty } from 'utils/itineraries/functions/builderUtils';

import { IAction } from '../ActionsMenu/types';
import { EDetailsMode } from '../types';

export function useDetailsTable() {
  const dispatch = useDispatch();
  const doesAppHaveChanges = useSelector(appHasChangesSelector);
  const itinerary = useSelector(itinerariesItemDataSelector);
  const scheduleItems = useSelector(getScheduleItemsSelector);
  const schedule = useSelector(getItineraryScheduleSelector);
  const isOrganizedByDay = schedule.type === EOrganizedBy.Day;
  const isOrganizedByDate = schedule.type === EOrganizedBy.Date;

  // sync days and dates on scheduleItems change
  React.useEffect(() => {
    if (!doesAppHaveChanges) return;

    if (isOrganizedByDay) dispatch(updateDatesByDaysAction());
    if (isOrganizedByDate) dispatch(updateDaysByDatesAction());
  }, [scheduleItems]);

  const switchModeActions: IAction[] = React.useMemo(
    () => [
      {
        title: 'Switch to the Calendar Builder',
        icon: ListBullets,
        tooltipText: 'You can’t switch the builder mode \n' + 'after events have been added.',
        onClick: async () => {
          try {
            sessionStorage.setItem('hideConfirmChangesForCurrentTab', 'true');
            await dispatch(updateItinerariesItemThunk(itinerary.id, { detailsMode: EDetailsMode.Chronological }));
          } catch (e) {
            sessionStorage.removeItem('hideConfirmChangesForCurrentTab');
          }
        },
        isDisabled: !isSchedulesEmpty(schedule.items),
      },
    ],
    [dispatch, schedule],
  );

  const handleSwitchToDate = React.useCallback(() => {
    dispatch(updateDatesByDaysAction());
    dispatch(setScheduleTypeAction(EOrganizedBy.Date));
  }, [dispatch]);

  const handleSwitchToDay = React.useCallback(() => {
    dispatch(updateDaysByDatesAction());
    dispatch(setScheduleTypeAction(EOrganizedBy.Day));
  }, [dispatch]);

  const organizedByActions: IAction[] = React.useMemo(() => {
    return [
      {
        id: EOrganizedBy.Day,
        title: 'Organize by Day',
        onClick: handleSwitchToDay,
        icon: isOrganizedByDay ? Check2 : '',
        isActive: isOrganizedByDay,
      },
      {
        id: EOrganizedBy.Date,
        title: 'Organize by Date',
        onClick: handleSwitchToDate,
        icon: isOrganizedByDate ? Check2 : '',
        isActive: isOrganizedByDate,
      },
    ];
  }, [dispatch, isOrganizedByDate, isOrganizedByDay, handleSwitchToDate, handleSwitchToDay]);

  return {
    switchModeActions,
    organizedByActions,
  };
}
